import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../services/axiosInterceptor.js';

const skuCheckboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const skuCheckboxCheckedIcon = <CheckBoxIcon fontSize="small" />;
const SKU_PAGE_SIZE = 50;

function shortSkuLabel(o) {
    if (o == null) return '';
    if (typeof o === 'string') return o;
    return o.sku || o.listingField || skuOptionLabel(o);
}

export function skuOptionLabel(o) {
    if (o == null) return '';
    if (typeof o === 'string') return o;
    const sku = o.sku || o.listingField || '';
    if (!sku) return '';
    const itemId = o.itemId ? ` · ${o.itemId}` : '';
    const category = o.category ? ` (${o.category})` : '';
    return `${sku}${itemId}${category}`;
}

export function skuObjectsFromTree(tree) {
    const list =
        Array.isArray(tree?.skus) && tree.skus.length
            ? tree.skus
            : tree?.sku
              ? [tree.sku]
              : [];
    return list.filter(Boolean).map((sku) =>
        typeof sku === 'string' ? { sku } : sku
    );
}

export function scopePayload({ scopeType, category, skus }) {
    const skuList = (skus || []).map((s) => s?.sku).filter(Boolean);
    const categoryValue =
        typeof category === 'string' ? category.trim() : String(category || '').trim();
    if (scopeType === 'sku') {
        return {
            scopeType: 'sku',
            sku: skuList[0] || '',
            skus: skuList,
            category: skus?.[0]?.category || categoryValue || '',
        };
    }
    return {
        scopeType: 'category',
        category: categoryValue,
        sku: '',
        skus: [],
    };
}

export function canSubmitScope({ scopeType, category, skus }) {
    if (scopeType === 'category') {
        return Boolean(typeof category === 'string' ? category.trim() : category);
    }
    return (skus || []).some((s) => s?.sku);
}

function mergeSkuOptions(base, incoming) {
    const merged = [...(base || [])];
    for (const o of incoming || []) {
        const already = merged.some(
            (s) => (s?.id && o.id === s.id) || (s?.sku && o.sku === s.sku)
        );
        if (!already) merged.push(o);
    }
    return merged;
}

export default function TreeScopeFields({
    scopeType,
    onScopeTypeChange,
    category,
    onCategoryChange,
    skus = [],
    onSkusChange,
    helperText,
}) {
    const [azCategories, setAzCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [categoryInput, setCategoryInput] = useState(
        typeof category === 'string' ? category : category ?? ''
    );
    const [skuSearch, setSkuSearch] = useState('');
    const [debouncedSkuSearch, setDebouncedSkuSearch] = useState('');
    const [skuResults, setSkuResults] = useState([]);
    const [skuPage, setSkuPage] = useState(1);
    const [skuHasMore, setSkuHasMore] = useState(false);
    const [skuTotal, setSkuTotal] = useState(0);
    const [loadingSkus, setLoadingSkus] = useState(false);
    const [loadingMoreSkus, setLoadingMoreSkus] = useState(false);
    const loadMoreLock = useRef(false);

    const loadCategories = useCallback(async () => {
        setLoadingCategories(true);
        try {
            const { data } = await api.get('/api/broylesllc/inspection-trees/meta/categories');
            setAzCategories(Array.isArray(data) ? data : []);
        } catch {
            setAzCategories([]);
        } finally {
            setLoadingCategories(false);
        }
    }, []);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    useEffect(() => {
        setCategoryInput(typeof category === 'string' ? category : category ?? '');
    }, [category]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSkuSearch(skuSearch.trim()), 300);
        return () => clearTimeout(t);
    }, [skuSearch]);

    const canSearchSku = debouncedSkuSearch.length >= 2;

    useEffect(() => {
        if (!canSearchSku || scopeType !== 'sku') {
            setSkuResults([]);
            setSkuPage(1);
            setSkuHasMore(false);
            setSkuTotal(0);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoadingSkus(true);
            setSkuPage(1);
            setSkuHasMore(false);
            try {
                const { data } = await api.get('/api/broylesllc/inspection-trees/meta/skus', {
                    params: {
                        search: debouncedSkuSearch,
                        limit: SKU_PAGE_SIZE,
                        page: 1,
                    },
                });
                if (cancelled) return;
                const items = Array.isArray(data?.items)
                    ? data.items
                    : Array.isArray(data)
                      ? data
                      : [];
                setSkuResults(items);
                setSkuTotal(Number(data?.total) || items.length);
                setSkuHasMore(Boolean(data?.hasMore));
                setSkuPage(1);
            } catch {
                if (!cancelled) {
                    setSkuResults([]);
                    setSkuTotal(0);
                    setSkuHasMore(false);
                }
            } finally {
                if (!cancelled) setLoadingSkus(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [debouncedSkuSearch, canSearchSku, scopeType]);

    const loadMoreSkus = useCallback(async () => {
        if (
            !canSearchSku ||
            scopeType !== 'sku' ||
            !skuHasMore ||
            loadingSkus ||
            loadingMoreSkus ||
            loadMoreLock.current
        ) {
            return;
        }
        loadMoreLock.current = true;
        setLoadingMoreSkus(true);
        const nextPage = skuPage + 1;
        try {
            const { data } = await api.get('/api/broylesllc/inspection-trees/meta/skus', {
                params: {
                    search: debouncedSkuSearch,
                    limit: SKU_PAGE_SIZE,
                    page: nextPage,
                },
            });
            const items = Array.isArray(data?.items)
                ? data.items
                : Array.isArray(data)
                  ? data
                  : [];
            setSkuResults((prev) => mergeSkuOptions(prev, items));
            setSkuTotal(Number(data?.total) || 0);
            setSkuHasMore(Boolean(data?.hasMore));
            setSkuPage(nextPage);
        } catch {
            /* keep current list */
        } finally {
            setLoadingMoreSkus(false);
            loadMoreLock.current = false;
        }
    }, [
        canSearchSku,
        scopeType,
        skuHasMore,
        loadingSkus,
        loadingMoreSkus,
        skuPage,
        debouncedSkuSearch,
    ]);

    const skuOptions = useMemo(
        () => mergeSkuOptions(skus, skuResults),
        [skuResults, skus]
    );

    const dialogCategoryOptions = useMemo(() => {
        const fromApi = Array.isArray(azCategories) ? azCategories : [];
        if (category && !fromApi.includes(category)) {
            return [category, ...fromApi];
        }
        return fromApi;
    }, [azCategories, category]);

    return (
        <>
            {helperText ? (
                <p className="insp-meta" style={{ marginBottom: 12 }}>
                    {helperText}
                </p>
            ) : null}
            <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={scopeType}
                onChange={(_, v) => v && onScopeTypeChange(v)}
                sx={{ mb: 2 }}
            >
                <ToggleButton value="category">AZ Category</ToggleButton>
                <ToggleButton value="sku">SKU</ToggleButton>
            </ToggleButtonGroup>

            {scopeType === 'category' ? (
                <Autocomplete
                    freeSolo
                    options={dialogCategoryOptions ?? []}
                    loading={loadingCategories}
                    value={category ?? ''}
                    inputValue={categoryInput}
                    onOpen={() => setCategoryInput('')}
                    onClose={() =>
                        setCategoryInput(typeof category === 'string' ? category : category ?? '')
                    }
                    onChange={(_, v) => onCategoryChange(v || null)}
                    onInputChange={(_, v, reason) => {
                        setCategoryInput(String(v ?? ''));
                        if (reason === 'input' || reason === 'clear') {
                            onCategoryChange(String(v ?? '') || null);
                        }
                    }}
                    getOptionLabel={(option) => String(option ?? '')}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="AZ Category"
                            margin="dense"
                            required
                            helperText={
                                loadingCategories
                                    ? 'Loading categories…'
                                    : dialogCategoryOptions.length
                                      ? 'Open the list to pick a different category, or type a new one'
                                      : 'No options loaded — type a category name'
                            }
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingCategories && <CircularProgress size={18} />}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            ) : (
                <div className="insp-sku-picker">
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        disableClearable
                        clearOnBlur={false}
                        forcePopupIcon
                        options={skuOptions ?? []}
                        loading={loadingSkus}
                        value={skus}
                        onChange={(_, v) => onSkusChange(Array.isArray(v) ? v : [])}
                        inputValue={skuSearch}
                        onInputChange={(_, v, reason) => {
                            // MUI clears the input on select/remove/reset; keep the query for bulk checks.
                            if (
                                reason === 'reset' ||
                                reason === 'selectOption' ||
                                reason === 'removeOption'
                            ) {
                                return;
                            }
                            setSkuSearch(String(v ?? ''));
                        }}
                        getOptionLabel={skuOptionLabel}
                        isOptionEqualToValue={(a, b) =>
                            Boolean(
                                (a?.id && b?.id && a.id === b.id) ||
                                    (a?.sku && b?.sku && a.sku === b.sku)
                            )
                        }
                        filterOptions={(x) => (Array.isArray(x) ? x : [])}
                        noOptionsText={
                            skuSearch.trim().length < 2 && skus.length === 0
                                ? 'Type at least 2 characters'
                                : loadingSkus
                                  ? 'Searching…'
                                  : 'No SKUs found'
                        }
                        // Chips live in the summary below — keep the search field single-line.
                        renderTags={() => null}
                        renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps } = props;
                            return (
                                <li key={key} {...optionProps}>
                                    <Checkbox
                                        icon={skuCheckboxIcon}
                                        checkedIcon={skuCheckboxCheckedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                        tabIndex={-1}
                                        disableRipple
                                    />
                                    <span className="insp-sku-option-label">
                                        {skuOptionLabel(option)}
                                    </span>
                                </li>
                            );
                        }}
                        ListboxProps={{
                            className: 'insp-sku-listbox',
                            style: { maxHeight: 260 },
                            onScroll: (event) => {
                                const el = event.currentTarget;
                                if (
                                    el.scrollHeight - el.scrollTop - el.clientHeight <
                                    48
                                ) {
                                    loadMoreSkus();
                                }
                            },
                        }}
                        sx={{
                            '& .MuiAutocomplete-inputRoot': {
                                flexWrap: 'nowrap',
                                py: 0.5,
                            },
                            '& .MuiAutocomplete-input': {
                                minWidth: '12ch !important',
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Search SKUs"
                                margin="dense"
                                required={skus.length === 0}
                                placeholder="Listing field…"
                                helperText={
                                    canSearchSku && skuTotal > 0
                                        ? `Showing ${Math.min(skuResults.length, skuTotal)} of ${skuTotal} — scroll for more`
                                        : 'Check SKUs in the list — selection stays below'
                                }
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {(loadingSkus || loadingMoreSkus) && (
                                                <CircularProgress size={18} />
                                            )}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {skus.length > 0 ? (
                        <div className="insp-sku-selected">
                            <div className="insp-sku-selected-head">
                                <span>
                                    <strong>{skus.length}</strong> SKU
                                    {skus.length === 1 ? '' : 's'} selected
                                </span>
                                <button
                                    type="button"
                                    className="insp-sku-clear"
                                    onClick={() => onSkusChange([])}
                                >
                                    Clear all
                                </button>
                            </div>
                            <ul className="insp-sku-selected-list">
                                {skus.map((s) => {
                                    const key = s?.id || s?.sku || shortSkuLabel(s);
                                    return (
                                        <li key={key} title={skuOptionLabel(s)}>
                                            <span>{shortSkuLabel(s)}</span>
                                            <IconButton
                                                size="small"
                                                aria-label={`Remove ${shortSkuLabel(s)}`}
                                                onClick={() =>
                                                    onSkusChange(
                                                        skus.filter(
                                                            (x) =>
                                                                !(
                                                                    (s?.id && x?.id === s.id) ||
                                                                    (s?.sku && x?.sku === s.sku)
                                                                )
                                                        )
                                                    )
                                                }
                                            >
                                                <CloseIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ) : null}
                </div>
            )}
        </>
    );
}
